import {
  css,
  styled,
  forBrands,
  SerializedStyles,
  StyleFn,
  getFont,
  getFontWeight,
} from "@core-ui/react-stitch";
import {BreakpointProviderState, MEDIUM} from "@core-ui/breakpoint-provider";
import {transitions} from "@core-ui/motion";
import {CommonProps} from "./types";

type PanelToggleButtonProps = CommonProps & {
  children?: React.ReactNode;
  expanded?: boolean;
  minWidth: BreakpointProviderState["minWidth"];
};

type PanelToggleStyleFn<
  Props = Partial<PanelToggleButtonProps>,
  Return = SerializedStyles
> = StyleFn<Props, Return>;

const getIconBgColor: PanelToggleStyleFn<
  Partial<PanelToggleButtonProps>,
  string
> = ({theme, crossBrand, unstyled}) => {
  const bgColor = forBrands(
    theme,
    {
      at: (t) => t.color.b1,
      default: (t) => t.color.bk,
      gap: (t) => t.color.b1,
      gapfs: (t) => t.color.b1,
      on: (t) => t.color.b1,
    },
    crossBrand
  ) as string;
  return unstyled ? theme.color.bk : bgColor;
};

const getBrandStyles: PanelToggleStyleFn<PanelToggleButtonProps, string> = ({
  theme,
  crossBrand,
  minWidth,
}) => {
  const sidePadding = minWidth(MEDIUM) ? "0" : "1rem";
  const brFontWeight = getFontWeight("regular");
  const brandStyles = forBrands(
    theme,
    {
      at: (t) => ({
        color: t.color.b1,
        padding: `1rem ${sidePadding}`,
      }),
      br: (t) => ({
        color: t.color.bk,
        padding: `1rem ${sidePadding}`,
        ...brFontWeight,
      }),
      brfs: (t) => ({
        color: t.color.bk,
        padding: `1rem ${sidePadding}`,
        ...brFontWeight,
      }),
      gap: (t) => ({
        color: t.color.b1,
        padding: `1rem ${sidePadding}`,
        textTransform: "capitalize",
      }),
      gapfs: (t) => ({
        color: t.color.b1,
        padding: `1rem ${sidePadding}`,
        textTransform: "capitalize",
      }),
      on: (t) => ({
        color: t.color.b1,
        fontSize: "1.0625rem",
        padding: `1rem ${sidePadding}`,
      }),
    },
    crossBrand
  ) as string;
  return brandStyles;
};

const sanitizeHexValue = (hex: string): string => hex.replace("#", "%23");

const chevronStyles: PanelToggleStyleFn<PanelToggleButtonProps> = ({
  expanded,
  crossBrand,
  minWidth,
  theme,
  unstyled,
}) => {
  const iconSpacing = minWidth(MEDIUM) ? "0.25rem" : "1rem";

  return css`
    ::before,
    ::after {
      background-repeat: no-repeat;
      background-position: center;
      background-size: 100% 100%;
      right: ${iconSpacing};
      height: 0.7rem;
      width: 0.7rem;
      top: 0;
      bottom: 0;
      margin-top: auto;
      margin-bottom: auto;
    }
    ::after {
      transition: transform
        ${expanded
          ? transitions.expressive.in.transitionDuration
          : transitions.performance.out.transitionDuration}
        ${expanded
          ? transitions.expressive.in.transitionTimingFunction
          : transitions.performance.out.transitionTimingFunction}
        ${transitions.userTriggeredDelay};
      display: block;
      transform-origin: center;
      transform: ${expanded ? "rotate(180deg)" : "rotate(0deg)"};
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='7' viewBox='0 0 11 7'%3E%3Cpath d='M1 1.5 L 5.5 5.5 L 10 1.5' fill='none' stroke='${sanitizeHexValue(
        getIconBgColor({theme, unstyled, crossBrand})
      )}' stroke-width='2'%3E%3C/path%3E%3C/svg%3E");
    }
  `;
};

const plusMinusStyles: PanelToggleStyleFn = ({
  theme,
  crossBrand,
  unstyled,
  expanded,
}) => css`
  ::before,
  ::after {
    background: ${getIconBgColor({theme, crossBrand, unstyled})};
  }
  ::before {
    display: block;
    width: 0.625rem;
    height: 0.125rem;
    right: 1.45rem;
    margin-top: -0.0625rem;
  }
  ::after {
    display: ${expanded ? "none" : "block"};
    width: 0.125rem;
    height: 0.625rem;
    right: 1.7rem;
    margin-top: -0.3125rem;
  }
`;

const fontStyles: PanelToggleStyleFn = ({theme, crossBrand}) => css`
  ${getFont("secondary")({theme, crossBrand})}
`;

const brandStyles: PanelToggleStyleFn<PanelToggleButtonProps> = ({
  theme,
  minWidth,
  crossBrand,
}) =>
  css`
    ${getBrandStyles({theme, minWidth, crossBrand})}
  `;

export const PanelToggleButton = styled.button<PanelToggleButtonProps>`
  ${fontStyles}
  appearance: none;
  display: block;
  position: relative;
  background-color: white;
  border-radius: 0;
  border: 0 none;
  font-size: 1rem;
  text-align: left;
  text-transform: uppercase;
  width: 100%;
  padding-top: 1rem;
  padding-bottom: 1rem;
  z-index: 1;
  ${({unstyled}) => (unstyled ? "" : brandStyles)}
  ${({sticky}) => (sticky ? "position: sticky; top: 0;" : "")} 
  ${({snapToTopOnClose}) => (snapToTopOnClose ? "top: 0;" : "")} 
  &::before,
  &::after {
    position: absolute;
    content: "";
    top: 50%;
  }

  ${({withPlusMinus}) => (withPlusMinus ? plusMinusStyles : chevronStyles)}
`;
